class RitualClassicPurchase extends HTMLElement {
  constructor(){
    super();
    this.variants=[];
    this.selected=[];
    this.imageOptionIndex=-1;
    this.deliveryTimer=null;
    this.weekdayNames=null;
    this.monthNames=null;
    this.sticky=null;
    this.stickyTriggerObserver=null;
    this.stickyFooterObserver=null;
    this.stickyPassed=false;
    this.stickyFooterVisible=false;
  }
  connectedCallback(){
    const source=this.querySelector('[data-rcp-variants]');
    if(!source)return;
    try{this.variants=JSON.parse(source.textContent||'[]')}catch(_){this.variants=[]}
    if(!this.variants.length)return;
    const initialId=Number(this.dataset.variantId||0);
    let initial=this.variants.find(v=>v.id===initialId)||this.variants.find(v=>v.available)||this.variants[0];
    this.selected=[...(initial?.options||[])];
    this.setupSizeSystems();
    initial=this.resolveInitialSizeVariant(initial)||initial;
    this.selected=[...(initial?.options||[])];
    this.parseDateLabels();
    this.imageOptionIndex=this.resolveImageOption();
    this.decorateImageOption();
    this.addEventListener('click',this.onClick);
    this.addEventListener('shopify:cart:lines-update',this.onCartLinesUpdate);
    // Preserve the theme's native gallery position on a normal PDP load.
    // Only an explicit ?variant=ID deep-link is allowed to select variant media
    // before the customer interacts with this picker.
    this.sync(initial,false);
    this.syncExplicitUrlVariant(initial);
    this.renderDeliveryDate();
    this.setupSticky();
  }
  disconnectedCallback(){
    this.removeEventListener('click',this.onClick);
    this.removeEventListener('shopify:cart:lines-update',this.onCartLinesUpdate);
    this.stickyTriggerObserver?.disconnect();
    this.stickyFooterObserver?.disconnect();
    if(this.deliveryTimer){clearTimeout(this.deliveryTimer);this.deliveryTimer=null}
  }

  setupSticky(){
    this.sticky=this.querySelector('[data-rcp-sticky]');
    if(!this.sticky)return;
    const trigger=this.querySelector('[data-rcp-submit]')||this.querySelector('.rcp');
    if(trigger){
      this.stickyTriggerObserver=new IntersectionObserver(([entry])=>{
        this.stickyPassed=!entry.isIntersecting&&entry.boundingClientRect.bottom<0;
        this.updateStickyVisibility();
      },{threshold:0});
      this.stickyTriggerObserver.observe(trigger);
    }
    const footerGroup=document.querySelector('.shopify-section-group-footer-group');
    const footer=footerGroup?.closest('footer')||footerGroup||document.querySelector('body > footer');
    if(footer){
      this.stickyFooterObserver=new IntersectionObserver(([entry])=>{
        this.stickyFooterVisible=entry.isIntersecting;
        this.updateStickyVisibility();
      },{threshold:0});
      this.stickyFooterObserver.observe(footer);
    }
  }
  updateStickyVisibility(){
    if(!this.sticky)return;
    const visible=this.stickyPassed&&!this.stickyFooterVisible;
    this.sticky.classList.toggle('is-visible',visible);
    this.sticky.setAttribute('aria-hidden',visible?'false':'true');
  }
  onStickyAction(){
    const target=this.querySelector('.rcp__picker')||this.querySelector('[data-rcp-submit]')||this.querySelector('.rcp')||this;
    const reduced=matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({behavior:reduced?'auto':'smooth',block:'center'});
    const focusTarget=target.querySelector?.('[data-rcp-value].is-selected:not(:disabled), [data-rcp-submit]');
    window.setTimeout(()=>focusTarget?.focus?.({preventScroll:true}),reduced?0:420);
  }

  onCartLinesUpdate=(event)=>{
    if(event.action!=='add'||!this.contains(event.target))return;
    const shouldCheckout=this.dataset.skipCart==='true';
    const shouldOpen=!shouldCheckout&&this.dataset.postAdd!=='stay';
    const autoOpenComponent=document.querySelector('cart-drawer-component[auto-open]');
    const suppressed=Boolean((shouldCheckout||!shouldOpen)&&autoOpenComponent);
    if(suppressed)autoOpenComponent.removeAttribute('auto-open');
    this.setBusy(true);

    const restore=()=>{
      if(suppressed&&autoOpenComponent?.isConnected)autoOpenComponent.setAttribute('auto-open','');
      this.setBusy(false);
    };
    const complete=(result)=>{
      if(result?.detail?.didError)return;
      if(shouldCheckout){window.location.assign(`${window.Shopify?.routes?.root||'/'}checkout`);return}
      if(shouldOpen)this.openCartDrawer();
    };

    if(event.promise?.then){
      event.promise.then(complete).catch(()=>{}).finally(restore);
    }else{
      queueMicrotask(()=>{if(shouldCheckout){window.location.assign(`${window.Shopify?.routes?.root||'/'}checkout`)}else if(shouldOpen)this.openCartDrawer();restore()});
    }
  }
  openCartDrawer(){
    requestAnimationFrame(()=>{
      const drawer=document.querySelector('theme-drawer#cart-drawer');
      if(typeof drawer?.open==='function'){drawer.open();return}
      document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
    });
  }
  setBusy(busy){
    const button=this.querySelector('[data-rcp-submit]');
    if(!button)return;
    const label=button.querySelector('[data-rcp-submit-label]');
    if(busy){
      button.disabled=true;
      button.setAttribute('aria-busy','true');
      if(label)label.textContent=this.dataset.loadingText||this.dataset.addText||'';
      return;
    }
    button.removeAttribute('aria-busy');
    const variant=this.variants.find(v=>String(v.id)===String(this.dataset.variantId));
    button.disabled=!variant?.available;
    if(label)label.textContent=variant?.available?(this.dataset.addText||''):(this.dataset.soldText||'');
  }
  setupSizeSystems(){
    this.querySelectorAll('[data-rcp-size-system]').forEach(fieldset=>{
      const tabs=[...fieldset.querySelectorAll('[data-rcp-size-tab]')];
      const choices=[...fieldset.querySelectorAll('[data-rcp-value]')];
      if(!tabs.length||!choices.length)return;
      tabs.forEach(tab=>{
        const prefix=String(tab.dataset.sizePrefix||'').trim();
        const has=Boolean(prefix)&&choices.some(choice=>this.sizePrefixMatches(choice.dataset.value||'',prefix));
        tab.hidden=!has;
      });
      const usable=tabs.filter(tab=>!tab.hidden);
      if(!usable.length){
        fieldset.dataset.rcpSizeReady='false';
        tabs.forEach(tab=>tab.hidden=true);
        return;
      }
      fieldset.dataset.rcpSizeReady='true';
      choices.forEach(choice=>{
        const raw=choice.dataset.value||'';
        const group=usable.find(tab=>this.sizePrefixMatches(raw,tab.dataset.sizePrefix||''));
        choice.dataset.sizeGroup=group?.dataset.sizeGroup||'';
        const label=choice.querySelector('.rcp__value-label');
        if(label&&group)label.textContent=this.stripSizePrefix(raw,group.dataset.sizePrefix||'');
      });
      const target=this.defaultSizeTab(fieldset,usable);
      fieldset.dataset.rcpSizeActive=target?.dataset.sizeGroup||usable[0].dataset.sizeGroup||'';
      this.paintSizeTabs(fieldset);
    });
  }
  sizePrefixPattern(prefix){
    const compact=String(prefix||'').trim().toLocaleLowerCase().replace(/[\s_\-–—:./]+/g,'');
    if(!compact)return null;
    const escaped=[...compact].map(char=>char.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')).join('[\\s_\\-–—:./]*');
    return new RegExp(`^${escaped}(?:[\\s_\\-–—:./]+|(?=\\d))`,'iu');
  }
  sizePrefixMatches(value,prefix){
    const pattern=this.sizePrefixPattern(prefix);
    return Boolean(pattern)&&pattern.test(String(value||'').trim());
  }
  stripSizePrefix(value,prefix){
    const raw=String(value||'').trim();
    const pattern=this.sizePrefixPattern(prefix);
    if(!pattern)return raw;
    const match=raw.match(pattern);
    return match?raw.slice(match[0].length).trim():raw;
  }
  selectedSizeTab(fieldset,tabs){
    const idx=Number(fieldset.dataset.optionIndex||0);
    const current=this.selected[idx]||'';
    return tabs.find(tab=>this.sizePrefixMatches(current,tab.dataset.sizePrefix||''))||null;
  }
  defaultSizeTab(fieldset,tabs){
    const forced=String(this.dataset.sizeDefault||'auto');
    if(forced!=='auto')return tabs.find(tab=>String(tab.dataset.sizeGroup)===forced)||tabs[0];
    const country=String(this.dataset.country||'').toUpperCase();
    const eu=new Set(['AT','BE','BG','HR','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','NO','CH','IS','LI','AE','SA','QA','KW','BH','OM']);
    let keys=[];
    if(country==='GB')keys=['uk','gb'];
    else if(country==='US'||country==='CA')keys=['us w','usw','women','us','us m','usm'];
    else if(country==='AU'||country==='NZ')keys=['uk','au'];
    else if(eu.has(country))keys=['eu','eur'];
    const normalized=tab=>`${tab.textContent||''} ${tab.dataset.sizePrefix||''}`.toLocaleLowerCase().replace(/[^a-z0-9]+/g,' ');
    for(const key of keys){
      const found=tabs.find(tab=>normalized(tab).includes(key.replace(/[^a-z0-9]+/g,' ')));
      if(found)return found;
    }
    return this.selectedSizeTab(fieldset,tabs)||tabs[0];
  }
  paintSizeTabs(fieldset){
    const active=String(fieldset.dataset.rcpSizeActive||'');
    fieldset.querySelectorAll('[data-rcp-size-tab]').forEach(tab=>{
      const selected=String(tab.dataset.sizeGroup)===active;
      tab.classList.toggle('is-selected',selected);
      tab.setAttribute('aria-selected',selected?'true':'false');
      tab.tabIndex=selected?0:-1;
    });
  }
  sizeChoiceAllowed(fieldset,choice){
    if(fieldset?.dataset.rcpSizeReady!=='true')return true;
    const active=String(fieldset.dataset.rcpSizeActive||'');
    return !choice.dataset.sizeGroup||String(choice.dataset.sizeGroup)===active;
  }
  displayOptionValue(fieldset,value){
    if(fieldset?.dataset.rcpSizeReady!=='true')return value||'';
    const tab=[...fieldset.querySelectorAll('[data-rcp-size-tab]')].find(item=>this.sizePrefixMatches(value,item.dataset.sizePrefix||''));
    return tab?this.stripSizePrefix(value,tab.dataset.sizePrefix||''):(value||'');
  }
  resolveInitialSizeVariant(initial){
    let current=initial;
    this.querySelectorAll('[data-rcp-size-system][data-rcp-size-ready="true"]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex||0);
      const active=String(fieldset.dataset.rcpSizeActive||'');
      const choices=[...fieldset.querySelectorAll('[data-rcp-value]')].filter(choice=>String(choice.dataset.sizeGroup||'')===active);
      if(!choices.length)return;
      const currentValue=current?.options?.[idx]||'';
      if(choices.some(choice=>(choice.dataset.value||'')===currentValue))return;
      const allowed=new Set(choices.map(choice=>choice.dataset.value||''));
      const selected=current?.options||this.selected||[];
      const candidates=this.variants.filter(v=>v.available&&allowed.has(v.options?.[idx]||''));
      candidates.sort((a,b)=>this.sizeVariantScore(b,selected,idx)-this.sizeVariantScore(a,selected,idx));
      current=candidates[0]||current;
    });
    return current;
  }
  sizeVariantScore(variant,selected,sizeIndex){
    return (variant?.available?1000:0)+(variant?.options||[]).reduce((score,value,index)=>score+(index!==sizeIndex&&value===selected?.[index]?1:0),0);
  }
  switchSizeSystem(tab){
    const fieldset=tab.closest('[data-rcp-size-system]');
    if(!fieldset||fieldset.dataset.rcpSizeReady!=='true'||tab.hidden)return;
    fieldset.dataset.rcpSizeActive=String(tab.dataset.sizeGroup||'');
    this.paintSizeTabs(fieldset);
    const idx=Number(fieldset.dataset.optionIndex||0);
    const choices=[...fieldset.querySelectorAll('[data-rcp-value]')];
    choices.forEach(choice=>choice.classList.toggle('is-size-hidden',!this.sizeChoiceAllowed(fieldset,choice)));
    const allowed=new Set(choices.filter(choice=>this.sizeChoiceAllowed(fieldset,choice)).map(choice=>choice.dataset.value||''));
    const candidates=this.variants.filter(v=>v.available&&allowed.has(v.options?.[idx]||''));
    candidates.sort((a,b)=>this.sizeVariantScore(b,this.selected,idx)-this.sizeVariantScore(a,this.selected,idx));
    const variant=candidates[0];
    if(variant){this.selected=[...variant.options];this.sync(variant,true)}
  }

  resolveImageOption(){
    const mode=this.dataset.imageOption||'auto';
    if(mode==='none')return -1;
    if(/^\d+$/.test(mode))return Math.max(-1,Number(mode)-1);
    const optionCount=Number(this.dataset.optionCount||0);
    let winner=-1,best=-1;
    for(let i=0;i<optionCount;i++){
      const values=[...new Set(this.variants.map(v=>v.options?.[i]).filter(Boolean))];
      const urls=new Set();let withImage=0;
      values.forEach(value=>{
        const v=this.representativeVariant(i,value);
        if(v?.image){withImage++;urls.add(v.image)}
      });
      if(urls.size<2)continue;
      const score=(urls.size*10)+withImage+(values.length/100);
      if(score>best){best=score;winner=i}
    }
    return winner;
  }
  representativeVariant(index,value){
    const previous=this.selected.slice(0,index);
    const matchesPrevious=v=>previous.every((selected,j)=>!selected||v.options?.[j]===selected);
    return this.variants.find(v=>v.available&&v.options?.[index]===value&&matchesPrevious(v)&&v.image)
      ||this.variants.find(v=>v.available&&v.options?.[index]===value&&v.image)
      ||this.variants.find(v=>v.options?.[index]===value&&v.image)
      ||null;
  }
  decorateImageOption(){
    this.querySelectorAll('[data-rcp-option]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex);
      const isImage=idx===this.imageOptionIndex;
      fieldset.classList.toggle('is-image-option',isImage);
      if(!isImage)return;
      fieldset.querySelectorAll('[data-rcp-value]').forEach(button=>this.paintSwatch(button,idx));
    });
  }
  paintSwatch(button,index){
    const v=this.representativeVariant(index,button.dataset.value||'');
    const holder=button.querySelector('[data-rcp-value-image]');
    if(!holder)return;
    if(v?.image){
      const current=holder.querySelector('img');
      if(current?.src&&current.src.includes(v.image))return;
      const img=document.createElement('img');
      img.src=v.image;img.alt='';img.loading='lazy';img.decoding='async';
      holder.replaceChildren(img);
    }else holder.replaceChildren();
  }
  onClick=(event)=>{
    const stickyAction=event.target.closest('[data-rcp-sticky-action]');
    if(stickyAction&&this.contains(stickyAction)){this.onStickyAction();return}
    const sizeTab=event.target.closest('[data-rcp-size-tab]');
    if(sizeTab&&this.contains(sizeTab)){this.switchSizeSystem(sizeTab);return}
    const button=event.target.closest('[data-rcp-value]');
    if(!button||button.disabled||!this.contains(button))return;
    const index=Number(button.dataset.optionIndex);
    const value=button.dataset.value||'';
    const proposal=[...this.selected];
    proposal[index]=value;
    const previous=proposal.slice(0,index);
    const candidates=this.variants.filter(v=>v.available&&v.options?.[index]===value&&previous.every((selected,j)=>!selected||v.options?.[j]===selected));
    if(!candidates.length)return;
    candidates.sort((a,b)=>this.score(b,proposal,index)-this.score(a,proposal,index));
    const variant=candidates[0];
    if(!variant)return;
    this.selected=[...variant.options];
    this.sync(variant,true);
  }
  exact(v,values){return values.every((value,index)=>v.options?.[index]===value)}
  score(v,values,clicked){
    let s=0;
    values.forEach((value,index)=>{if(index!==clicked&&value&&v.options?.[index]===value)s++});
    return s;
  }
  optionState(index,value){
    const previous=this.selected.slice(0,index);
    const matchesPrefix=v=>previous.every((selected,j)=>!selected||v.options?.[j]===selected)&&v.options?.[index]===value;
    return {
      exists:this.variants.some(matchesPrefix),
      available:this.variants.some(v=>v.available&&matchesPrefix(v))
    };
  }
  sync(variant,interactive){
    if(!variant)return;
    this.dataset.variantId=String(variant.id);
    this.selected=[...variant.options];
    this.querySelectorAll('[data-rcp-option]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex);
      const selectedValue=this.selected[idx]||'';
      const selectedLabel=fieldset.querySelector('[data-rcp-selected-value]');
      if(selectedLabel)selectedLabel.textContent=this.displayOptionValue(fieldset,selectedValue);
      fieldset.querySelectorAll('[data-rcp-value]').forEach(button=>{
        const value=button.dataset.value||'';
        const state=this.optionState(idx,value);
        const chosen=value===selectedValue;
        button.classList.toggle('is-selected',chosen);
        button.classList.toggle('is-nonexistent',!state.exists);
        button.classList.toggle('is-size-hidden',!this.sizeChoiceAllowed(fieldset,button));
        button.classList.toggle('is-sold-out',state.exists&&!state.available);
        button.setAttribute('aria-pressed',chosen?'true':'false');
        button.disabled=!state.available&&!chosen;
        button.setAttribute('aria-disabled',button.disabled?'true':'false');
        if(idx===this.imageOptionIndex)this.paintSwatch(button,idx);
      });
    });
    const current=this.querySelector('[data-rcp-current-price]');
    const compare=this.querySelector('[data-rcp-compare-price]');
    if(current)current.textContent=variant.priceFormatted||'';
    if(compare){
      const show=Number(variant.compareAt||0)>Number(variant.price||0)&&variant.compareFormatted;
      compare.textContent=show?variant.compareFormatted:'';
      compare.hidden=!show;
    }
    this.updateDiscountBadge(variant);
    const input=this.querySelector('input[name="id"]');if(input)input.value=String(variant.id);
    const button=this.querySelector('[data-rcp-submit]');
    if(button){
      button.disabled=!variant.available;
      const label=button.querySelector('[data-rcp-submit-label]');
      if(label)label.textContent=variant.available?(this.dataset.addText||''):(this.dataset.soldText||'');
    }
    const addContainer=this.querySelector('add-to-cart-component');
    if(addContainer&&variant.image)addContainer.dataset.productVariantMedia=variant.image;
    if(interactive){this.syncUrl(variant);this.syncGallery(variant)}
  }
  syncUrl(variant){
    try{const url=new URL(window.location.href);url.searchParams.set('variant',variant.id);history.replaceState({},'',url.toString())}catch(_){}
  }
  syncExplicitUrlVariant(variant){
    // Do not jump from editorial/universal product media to the first available
    // variant during a normal page load. Deep links are the only init-time
    // exception because the visitor explicitly requested that variant.
    if(!variant?.mediaId)return;
    let explicitId=0;
    try{explicitId=Number(new URL(window.location.href).searchParams.get('variant')||0)}catch(_){}
    if(!explicitId||explicitId!==Number(variant.id))return;
    requestAnimationFrame(()=>this.syncGallery(variant,{animate:false}));
  }
  syncGallery(variant,{animate=true}={}){
    if(!variant?.mediaId)return;
    const scope=this.closest('.shopify-section')||document;
    const gallery=scope.querySelector('media-gallery')||document.querySelector('media-gallery');
    if(!gallery)return;
    const selectMedia=()=>{
      const slides=[...(gallery.querySelectorAll('slideshow-slide')||[])];
      const idx=slides.findIndex(slide=>slide.querySelector(`[data-media-id="${variant.mediaId}"]`));
      if(idx>-1&&gallery.slideshow?.select){
        gallery.slideshow.select(idx,undefined,{animate});
        gallery.preloadImage?.(idx);
        return true;
      }
      const media=gallery.querySelector(`[data-media-id="${variant.mediaId}"]`);
      if(media){media.scrollIntoView?.({behavior:'smooth',block:'nearest',inline:'nearest'});return true}
      return false;
    };
    if(!selectMedia())requestAnimationFrame(selectMedia);
  }
  parseDateLabels(){
    const source=this.querySelector('[data-rcp-date-labels]');
    if(!source)return;
    try{
      const data=JSON.parse(source.textContent||'{}');
      this.weekdayNames=this.parseDateNames(data.weekdays,7);
      this.monthNames=this.parseDateNames(data.months,12);
    }catch(_){this.weekdayNames=null;this.monthNames=null}
  }
  parseDateNames(value,count){
    const raw=String(value||'').trim();
    if(!raw)return null;
    // Comma-separated lists are the canonical merchant/Translate & Adapt format.
    // Newlines remain accepted only as a backwards-compatible migration path.
    const parts=(raw.includes(',')?raw.split(','):raw.split(/\n+/)).map(v=>v.trim()).filter(Boolean);
    return parts.length===count?parts:null;
  }
  formatDeliveryDate(date){
    const locale=document.documentElement.lang||this.dataset.locale||navigator.language||'en';
    try{
      const formatter=new Intl.DateTimeFormat(locale,{weekday:'short',month:'long',day:'numeric'});
      const parts=formatter.formatToParts(date).map(part=>{
        if(part.type==='weekday'&&this.weekdayNames)return {...part,value:this.weekdayNames[date.getDay()]};
        if(part.type==='month'&&this.monthNames)return {...part,value:this.monthNames[date.getMonth()]};
        return part;
      });
      return parts.map(part=>part.value).join('');
    }catch(_){return date.toLocaleDateString(locale)}
  }
  money(cents){
    const locale=document.documentElement.lang||this.dataset.locale||navigator.language||'en';
    const currency=window.Shopify?.currency?.active||'USD';
    try{return new Intl.NumberFormat(locale,{style:'currency',currency}).format(Number(cents||0)/100)}catch(_){return String(cents||0)}
  }
  updateDiscountBadge(variant){
    const badge=this.querySelector('[data-rcp-discount-badge]');
    if(!badge)return;
    const mode=this.dataset.discountBadgeMode||'percentage';
    const compare=Number(variant?.compareAt||0),price=Number(variant?.price||0);
    const discount=Math.max(0,compare-price);
    const prefix=String(this.dataset.discountBadgePrefix||'').trim();
    let text='';
    if(mode==='custom')text=String(this.dataset.discountBadgeCustom||'').trim();
    else if(discount>0&&compare>0){
      const value=mode==='amount'?this.money(discount):`${Math.round((discount/compare)*100)}%`;
      text=[prefix,value].filter(Boolean).join(' ');
    }
    badge.textContent=text;
    badge.hidden=!text;
  }
  scheduleDeliveryRefresh(){
    if(this.deliveryTimer)clearTimeout(this.deliveryTimer);
    const now=new Date();
    const next=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1,0,0,2,0);
    const delay=Math.max(60000,next.getTime()-now.getTime());
    this.deliveryTimer=setTimeout(()=>this.renderDeliveryDate(),delay);
  }
  renderDeliveryDate(){
    const target=this.querySelector('[data-rcp-delivery-date]');if(!target)return;
    const date=new Date();
    date.setHours(12,0,0,0);
    let days=Math.max(0,Number(this.dataset.processingDays||1));
    const business=this.dataset.skipWeekends==='true';
    while(days>0){date.setDate(date.getDate()+1);if(!business||![0,6].includes(date.getDay()))days--}
    target.textContent=this.formatDeliveryDate(date);
    this.scheduleDeliveryRefresh();
  }
}
if(!customElements.get('ritual-classic-purchase'))customElements.define('ritual-classic-purchase',RitualClassicPurchase);
